import FeatureLandingPage from "@/components/features/FeatureLandingPage";

const SITE = "https://shattaraai.com";

const howItWorks = [
  { title: "Select Your Material", description: "Choose from your uploaded PDFs, videos, or lecture notes — or paste content directly." },
  { title: "AI Creates Questions", description: "Shattara generates multiple-choice, true/false, and open-ended questions tailored to your content." },
  { title: "Take the Quiz", description: "Answer questions interactively. Get instant feedback with explanations for every answer." },
];

const benefits = [
  { title: "Practice with Your Actual Content", description: "Quizzes are generated from your uploaded materials, so every question is relevant to your course." },
  { title: "Multiple Question Types", description: "Get a mix of MCQ, true/false, and short-answer questions to test different levels of understanding." },
  { title: "Instant Feedback", description: "See which answers were correct and read detailed explanations immediately after each question." },
  { title: "Identify Knowledge Gaps", description: "Quiz results highlight exactly which topics need more review." },
  { title: "Unlimited Retakes", description: "Practice as many times as you need. Each attempt helps reinforce learning." },
];

const faqItems = [
  { question: "What types of questions does the AI generate?", answer: "Shattara generates multiple-choice, true/false, and open-ended questions. You can choose which types to include when creating a quiz." },
  { question: "Are the questions based on my materials?", answer: "Yes. Every question is generated from your uploaded content — PDFs, slides, transcripts, or notes." },
  { question: "Can I retake a quiz?", answer: "Absolutely. You can retake any quiz as many times as you want. Shattara tracks your score history so you can see improvement." },
  { question: "How is this different from the exam generator?", answer: "Quizzes are shorter, topic-focused practice sessions. Exams simulate full test conditions with timing, broader coverage, and a final score report." },
  { question: "Is the quiz generator free?", answer: "Free accounts can generate a limited number of quizzes per day. Paid plans remove limits and unlock advanced question types." },
];

const relatedFeatures = [
  { name: "AI Tutor", href: "/ai-tutor", description: "Get personalized explanations for any concept you're struggling with." },
  { name: "AI Flashcard Generator", href: "/ai-flashcards", description: "Turn your notes into spaced-repetition flashcards automatically." },
  { name: "AI Exam Generator", href: "/ai-exam-generator", description: "Simulate full exams with timing and comprehensive scoring." },
  { name: "AI Lecture Summarizer", href: "/ai-lecture-summarizer", description: "Summarize hours of recorded lectures into concise notes." },
  { name: "Best AI Study Tools", href: "/best-ai-study-tools", description: "Compare the top AI-powered study tools for students in 2026." },
];

const structuredData = [
  {
    "@context": "https://schema.org",
    "@type": "Product",
    name: "Shattara AI Quiz Generator",
    description: "Generate practice quizzes with multiple question types from your own study materials.",
    url: `${SITE}/ai-quiz-generator`,
    brand: { "@type": "Organization", name: "Shattara AI" },
    offers: { "@type": "Offer", price: "0", priceCurrency: "USD", availability: "https://schema.org/InStock" },
  },
  {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqItems.map((item) => ({
      "@type": "Question",
      name: item.question,
      acceptedAnswer: { "@type": "Answer", text: item.answer },
    })),
  },
  {
    "@context": "https://schema.org",
    "@type": "HowTo",
    name: "How to Generate AI Quizzes with Shattara",
    description: "Create practice quizzes from your course materials using Shattara AI.",
    step: howItWorks.map((s, i) => ({
      "@type": "HowToStep",
      position: i + 1,
      name: s.title,
      text: s.description,
    })),
  },
];

export default function AIQuizGeneratorPage() {
  return (
    <FeatureLandingPage
      seoTitle="AI Quiz Generator — Practice from Your Notes | Shattara AI"
      seoDescription="Create practice quizzes from your PDFs, slides, and lectures. Shattara AI generates MCQ, true/false, and open-ended questions with instant feedback."
      headline="AI Quiz Generator — Test Yourself from Your Own Notes"
      subtitle="Select your materials and Shattara creates targeted practice quizzes with instant feedback and explanations."
      howItWorks={howItWorks}
      benefits={benefits}
      faqItems={faqItems}
      relatedFeatures={relatedFeatures}
      structuredData={structuredData}
    />
  );
}
